import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, BriefcaseIcon, CheckIcon, ClockIcon, ShieldCheckIcon, SparkIcon } from "@/components/icons";
import { JobCard } from "@/components/job-card";
import { SearchBox } from "@/components/search-box";
import { demoJobs } from "@/lib/demo-data";
import { compactNumber, initials, jobTitle, relativeDate } from "@/lib/format";
import type { JobSummary } from "@/lib/types";
import { getJobCount, searchJobs } from "@/src/api";
import { useDocumentMeta } from "@/src/use-document-meta";

const popularSearches = [
  ["Software engineering", "software engineer"],
  ["Product", "product manager"],
  ["Design", "designer"],
  ["Data", "data analyst"],
  ["Marketing", "marketing"],
];

export function HomePage() {
  const [jobs, setJobs] = useState<JobSummary[]>(window.__CHUNO_HOME__?.jobs?.length ? window.__CHUNO_HOME__.jobs : demoJobs);
  const [count, setCount] = useState(window.__CHUNO_HOME__?.count || 500);

  useDocumentMeta({
    title: "Chuno — Job search across India",
    description: "Chuno is a job-discovery service for people seeking work in India. Search current openings, review role details, and manage your Apply activity.",
    canonical: "https://www.chuno.work/",
  });

  useEffect(() => {
    const controller = new AbortController();
    Promise.all([
      searchJobs({ limit: 6, signal: controller.signal }),
      getJobCount(controller.signal),
    ]).then(([response, liveCount]) => {
      if (response.hits.length) setJobs(response.hits);
      setCount(liveCount);
    }).catch(() => undefined);
    return () => controller.abort();
  }, []);

  const showcase = jobs.slice(0, 3);

  return (
    <main id="main">
      <section className="hero">
        <div className="hero__glow" aria-hidden="true" />
        <div className="shell hero__inner">
          <div className="hero__copy">
            <p className="eyebrow"><span /> Chuno · Job search across India</p>
            <h1>Find the right job,<br /><em>without the noise.</em></h1>
            <p className="hero__lede">Chuno is a job-discovery service for people seeking work in India. Search the latest {compactNumber(count)} openings, review clear role details, and manage your Apply activity in a private account.</p>
            <SearchBox />
            <div className="popular-searches" aria-label="Popular searches">
              <span>Popular:</span>
              {popularSearches.map(([label, query]) => (
                <Link key={query} to={`/jobs?q=${encodeURIComponent(query)}`}>{label}</Link>
              ))}
            </div>
          </div>

          <div className="hero-signal" aria-label="Current job highlights">
            <div className="hero-signal__head">
              <div><span className="live-dot" /><p>New on Chuno</p></div>
              <span>Updated today</span>
            </div>
            <div className="signal-list">
              {showcase.map((job, index) => (
                <Link to={`/jobs/${encodeURIComponent(job.job_id)}`} className="signal-job" reloadDocument key={job.job_id}>
                  <div className={`signal-job__avatar signal-job__avatar--${index + 1}`}>{initials(job.company)}</div>
                  <div><strong>{jobTitle(job)}</strong><span>{job.company} · {job.city || job.location || "India"}</span></div>
                  <ArrowUpRight />
                </Link>
              ))}
            </div>
            <div className="signal-summary">
              <div><strong>{compactNumber(count)}</strong><span>latest open roles</span></div>
              <div><strong>India</strong><span>nationwide</span></div>
              <div><strong>Live</strong><span>source checks</span></div>
            </div>
          </div>
        </div>
      </section>

      <section className="trust-strip" aria-label="Chuno principles">
        <div className="shell trust-strip__inner">
          <p><ShieldCheckIcon /> Current openings</p>
          <p><BriefcaseIcon /> Jobs across India</p>
          <p><ClockIcon /> Regularly refreshed</p>
          <p><CheckIcon /> Source-aware listings</p>
        </div>
      </section>

      <section className="section roles-section">
        <div className="shell">
          <div className="section-heading">
            <div><p className="section-kicker">Open now</p><h2>Worth a closer look.</h2></div>
            <Link className="text-link" to="/jobs">See all openings <ArrowRight /></Link>
          </div>
          <div className="job-grid">
            {jobs.map((job, index) => <JobCard job={job} featured={index === 0} key={job.job_id} />)}
          </div>
        </div>
      </section>

      <section className="section why-section" id="why-chuno">
        <div className="shell why-grid">
          <div className="why-intro">
            <p className="section-kicker section-kicker--light">Why Chuno</p>
            <h2>Good choices start with better information.</h2>
            <p>Job search is demanding enough. Chuno keeps the experience focused, transparent, and current.</p>
            <Link to="/about">How Chuno works <ArrowUpRight /></Link>
          </div>
          <div className="principles">
            <article><span>01</span><ShieldCheckIcon /><h3>Current by default</h3><p>Listings are refreshed against hiring sources so you spend time on roles that are still moving.</p></article>
            <article><span>02</span><SparkIcon /><h3>Signal over volume</h3><p>Relevant details stay visible. Reposts, clutter, and engagement tricks stay out of your way.</p></article>
            <article><span>03</span><ArrowUpRight /><h3>A clear next step</h3><p>Understand the role, assess the fit, and continue to the application path with confidence.</p></article>
          </div>
        </div>
      </section>

      <section className="section choose-section">
        <div className="shell choose-card">
          <div className="choose-card__mark" aria-hidden="true">चुनो</div>
          <div><p className="section-kicker">Choose what fits</p><h2>Your next move should feel considered—not random.</h2></div>
          <Link className="button button--lime" to="/jobs">Explore current jobs <ArrowUpRight /></Link>
        </div>
      </section>

      <section className="fresh-note">
        <div className="shell fresh-note__inner">
          <CheckIcon />
          <p><strong>Latest signal:</strong> {jobTitle(jobs[0])} at {jobs[0]?.company} — {relativeDate(jobs[0]?.last_seen_date).toLowerCase()}.</p>
        </div>
      </section>
    </main>
  );
}
